import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import {
  Button,
  Icon,
  Layout,
  Loading,
  Pane,
  PaneFooter,
  Tooltip,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import useCubeMetaAPI from '../../../../hooks/useCubeMetaAPI';
import useFieldLabelsAPI from '../../../../hooks/useFieldLabelsAPI';
import ViewSectionSelector from './ViewSectionSelector';
import { resolveTimeDimension, buildFieldAliasMap } from './utils/viewMetaUtils';
import {
  generateQueryFromFormState,
  getDefaultFormValues,
  getInitialValuesFromURL,
  cleanFormStateForURL,
} from './utils/viewBuilderUtils';

/**
 * ViewReportBuilder — unified builder component for all view-based builders.
 *
 * Replaces:
 *   CatalogingReportBuilder + 4 Selector components
 *   CirculationReportBuilder + 4 Selector components
 *   ProductivityReportBuilder (partially)
 *
 * Props:
 *  - viewName       : Cube.js view name (e.g. "cataloging_view")
 *  - sections       : array of section configs
 *  - builderTitle   : pane title node
 *  - timeDimension  : optional time dimension for date range filter
 *  - queryOptions   : extra options passed to generateQueryFromFormState
 *  - showBuilderPane / setShowBuilderPane
 *  - setQuery / setFormState / formState
 *  - isLoading
 *  - handleClose
 */
function ViewReportBuilder({
  viewName,
  sections,
  builderTitle,
  timeDimension,
  queryOptions,
  showBuilderPane,
  setShowBuilderPane,
  setQuery,
  setFormState,
  formState,
  isLoading,
  handleClose,
}) {
  const intl = useIntl();

  const { response: metaResponse, isLoading: metaLoading } = useCubeMetaAPI();
  const { labelsMap: dimLabels } = useFieldLabelsAPI({ fieldType: 'dimension' });
  const { labelsMap: measLabels } = useFieldLabelsAPI({ fieldType: 'measure' });
  const labelsMap = { ...dimLabels, ...measLabels };

  const fieldAliasMap = useMemo(
    () => buildFieldAliasMap(metaResponse, viewName),
    [metaResponse, viewName],
  );

  const initialValues = useMemo(
    () => getInitialValuesFromURL(sections, labelsMap, fieldAliasMap),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labelsMap, fieldAliasMap],
  );

  // Auto-execute if form state exists in URL — runs when fieldAliasMap is ready
  useEffect(() => {
    if (!formState || !fieldAliasMap || Object.keys(fieldAliasMap).length === 0) return;
    if (!Object.keys(initialValues).some((k) => initialValues[k])) return;

    const resolvedTime = resolveTimeDimension(timeDimension, metaResponse, viewName);
    const query = generateQueryFromFormState(initialValues, sections, {
      timeDimension: resolvedTime,
      fieldAliasMap,
      ...queryOptions,
    });
    if (query.dimensions.length > 0 || query.measures.length > 0) {
      setQuery(query);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldAliasMap]);

  const onSubmit = (values, form) => {
    const resolvedTime = resolveTimeDimension(timeDimension, metaResponse, viewName);
    const fieldAliasMap = buildFieldAliasMap(metaResponse, viewName);
    const query = generateQueryFromFormState(values, sections, {
      timeDimension: resolvedTime,
      fieldAliasMap,
      ...queryOptions,
    });

    if (!query.dimensions.length && !query.measures.length) return;

    setFormState(JSON.stringify(cleanFormStateForURL(values, sections)));
    setQuery(query, {
      onSuccess: () => form.initialize(values),
    });
  };

  const onReset = (form) => {
    setFormState('');
    setQuery('');
    form.reset(getDefaultFormValues(sections));
  };

  const onCancel = () => setQuery('');

  if (metaLoading) {
    return (
      <Layout className="centered full" style={{ height: '8rem' }}>
        <Loading size="xlarge" />
      </Layout>
    );
  }

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      mutators={{ ...arrayMutators }}
      subscription={{ values: true, submitting: true, pristine: true }}
    >
      {({ handleSubmit, form, submitting, values, pristine }) => {
        const hasFields = sections.some((s) => {
          const v = values[s.formField];
          return Array.isArray(v) && v.length > 0;
        });

        return (
          <>
            {showBuilderPane ? (
              <Pane
                id={`pane-${viewName}-builder`}
                appIcon={<Icon icon="catalog" size="large" />}
                defaultWidth="30%"
                fluidContentWidth
                centeredContent
                paneTitle={builderTitle}
                footer={
                  <PaneFooter
                    renderEnd={
                      isLoading ? (
                        <Button
                          buttonStyle="danger"
                          marginBottom0
                          onClick={onCancel}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <FormattedMessage
                            id="ui-reports.viewBuilder.stopLoading"
                            defaultMessage="Stop Loading"
                          />
                        </Button>
                      ) : (
                        <Button
                          buttonStyle="primary"
                          disabled={submitting || !hasFields}
                          marginBottom0
                          onClick={() => handleSubmit()}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <FormattedMessage
                            id="ui-reports.viewBuilder.generateReport"
                            defaultMessage="Generate Report"
                          />
                        </Button>
                      )
                    }
                    renderStart={
                      <Button
                        buttonStyle="primary"
                        disabled={submitting || !hasFields || isLoading}
                        marginBottom0
                        onClick={() => onReset(form)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <FormattedMessage
                          id="ui-reports.viewBuilder.reset"
                          defaultMessage="Reset"
                        />
                      </Button>
                    }
                  />
                }
                lastMenu={
                  <Tooltip
                    id={`${viewName}-hide-pane-tooltip`}
                    text={
                      <FormattedMessage
                        id="ui-reports.viewBuilder.hidePane"
                        defaultMessage="Hide builder pane"
                      />
                    }
                  >
                    {({ ref, ariaIds }) => (
                      <Button
                        buttonStyle="dropdownItem"
                        marginBottom0
                        onClick={() => setShowBuilderPane(false)}
                        aria-labelledby={ariaIds.text}
                        ref={ref}
                      >
                        <Icon icon="chevron-double-left" />
                      </Button>
                    )}
                  </Tooltip>
                }
              >
                <form onSubmit={handleSubmit}>
                  {sections.map((section) => (
                    <ViewSectionSelector
                      key={section.formField}
                      section={section}
                      viewName={viewName}
                      metaResponse={metaResponse}
                      labelsMap={labelsMap}
                      fieldAliasMap={fieldAliasMap}
                      formValues={values}
                    />
                  ))}
                </form>
              </Pane>
            ) : (
              <div />
            )}
          </>
        );
      }}
    </Form>
  );
}

ViewReportBuilder.propTypes = {
  viewName: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      formField: PropTypes.string.isRequired,
      filtersField: PropTypes.string,
      label: PropTypes.node.isRequired,
      filterCfg: PropTypes.object,
      fieldType: PropTypes.oneOf(['dimension', 'measure']),
    }),
  ).isRequired,
  builderTitle: PropTypes.node.isRequired,
  timeDimension: PropTypes.string,
  queryOptions: PropTypes.object,
  showBuilderPane: PropTypes.bool.isRequired,
  setShowBuilderPane: PropTypes.func.isRequired,
  setQuery: PropTypes.func.isRequired,
  setFormState: PropTypes.func.isRequired,
  formState: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ViewReportBuilder;
